import { NavLink, Outlet, useNavigate } from 'react-router-dom';

import { useLogout, useSession } from '@/lib/session';
import { may } from '@/lib/may';
import { cn } from '@/lib/cn';
import { Icon, type IconName } from '@/components/icons/Icon';
import { InboxBell } from '@/features/admin/InboxBell';
import { useInbox } from '@/features/admin/useInbox';
import { Logo } from './BuyerLayout';

/**
 * The admin console shell.
 *
 * §6: buyer, seller and admin are role-gated route groups in ONE app, which is
 * why this is a layout rather than a separate build.
 *
 * Deep blue chrome, dense content, no marketing. The admin mockups describe
 * these as screens "an operator sits in for six hours", so the navigation gets
 * out of the way and stays out of it.
 */

/**
 * The console's rooms, and what each one takes to enter.
 *
 * `permission` is the SAME string the route's `can:` middleware checks on the
 * server, so the menu cannot offer a screen the gate would refuse — and cannot
 * hide one it would allow. When somebody's role is widened, their menu grows
 * on the next page load without anybody editing this list.
 */
const adminNav: Array<{
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
  /** One permission, or ANY of several — see the blog desk. */
  permission: string | string[];
}> = [
  { to: '/admin', label: 'Dashboard', icon: 'chart', end: true, permission: 'dashboard.read' },
  // Second, under the dashboard. It is the only screen in this console
  // with a person waiting at the other end of it.
  { to: '/admin/support', label: 'Support', icon: 'message', permission: 'support.handle' },
  { to: '/admin/clinical', label: 'Reactions', icon: 'alert', permission: 'clinical.read' },
  { to: '/admin/settings', label: 'Settings', icon: 'settings', permission: 'settings.edit' },
  { to: '/admin/shelf-life', label: 'Shelf life', icon: 'clock', permission: 'shelf_life.edit' },
  { to: '/admin/pages', label: 'Pages', icon: 'doc', permission: 'pages.edit' },
  // Either grant. A content writer holds `posts.write` and an editor whose only
  // job is to review and publish holds `posts.publish`; both need the menu, and
  // the route behind it admits both.
  {
    to: '/admin/blog',
    label: 'Blog',
    icon: 'quote',
    permission: ['posts.write', 'posts.publish'],
  },
  { to: '/admin/shopfront', label: 'Shop front', icon: 'star', permission: 'pages.edit' },
  { to: '/admin/catalogue', label: 'Catalogue', icon: 'grid', end: true, permission: 'catalogue.read' },
  { to: '/admin/catalogue/requests', label: 'Requests', icon: 'doc', permission: 'catalogue.read' },
  { to: '/admin/pharmacies', label: 'Pharmacies', icon: 'store', permission: 'pharmacies.read' },
  { to: '/admin/recalls', label: 'Recalls', icon: 'alert', permission: 'recalls.issue' },
  { to: '/admin/withdrawn', label: 'Withdrawn', icon: 'flag', permission: 'prohibitions.issue' },
  { to: '/admin/users', label: 'People', icon: 'user', permission: 'people.read' },
  { to: '/admin/roles', label: 'Access', icon: 'shield', permission: 'people.manage' },
  { to: '/admin/advertising', label: 'Advertising', icon: 'star', permission: 'ads.manage' },
  { to: '/admin/disclosures', label: 'Disclosures', icon: 'doc', permission: 'audit.disclose' },
  { to: '/admin/refunds', label: 'Refunds', icon: 'money', permission: 'refunds.read' },
  { to: '/admin/collections', label: 'Collections', icon: 'chart', permission: 'collections.read' },
  { to: '/admin/prescriptions', label: 'Prescriptions', icon: 'doc', permission: 'prescriptions.read' },
  { to: '/admin/delivery', label: 'Delivery', icon: 'truck', permission: 'delivery.edit' },
];

export function AdminLayout() {
  const { user } = useSession();

  // The menu offers exactly what the gates allow. A room this person's role
  // does not cover is not listed for them — and one it does is.
  const visible = adminNav.filter(
    (item) =>
      user &&
      (Array.isArray(item.permission)
        ? item.permission.some((one) => may(user, one))
        : may(user, item.permission)),
  );
  /**
   * ── THE NUMBER ON EACH DESK ─────────────────────────────────────────
   *
   * The bell says how much is waiting; this says WHERE. Both read the same
   * query — React Query dedupes by key, so it is one request and the badge
   * beside "Support" can never disagree with the number in the bell.
   *
   * This is the half that answers the actual complaint. A bell tells somebody
   * there is work; a badge on the menu tells them which room it is in, without
   * opening anything. Before this, finding out meant visiting all seventeen
   * screens in turn — so in practice it meant visiting the ones you thought
   * to check.
   */
  const { data: inbox } = useInbox();

  const waiting = new Map(
    (inbox?.groups ?? []).map((group) => [group.url, group]),
  );

  const logout = useLogout();
  const navigate = useNavigate();

  const signOut = () =>
    logout.mutate(undefined, { onSettled: () => navigate('/admin/login', { replace: true }) });

  return (
    <div className="flex min-h-dvh bg-paper">
      <nav
        aria-label="Admin"
        className="hidden w-[236px] shrink-0 flex-col gap-[3px] bg-ink p-3 pt-[18px] md:flex"
      >
        <span className="flex items-center gap-1.5 px-2 pb-[18px] font-display text-[17px] font-extrabold tracking-[-0.02em] text-white">
          <span className="grid h-[22px] w-[22px] place-items-center rounded-[7px] bg-white/15">
            <Logo className="h-[13px] w-[13px] fill-white" />
          </span>
          ChiCare
          <span className="text-[10px] font-medium tracking-[0.1em] opacity-60">ADMIN</span>
        </span>

        {visible.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-2.5 rounded-[9px] px-3 py-2.5 text-[13px] font-medium no-underline',
                isActive ? 'bg-white/[0.13] font-bold text-white' : 'text-[#B9CBEC] hover:text-white',
              )
            }
          >
            <Icon name={item.icon} size={17} strokeWidth={1.8} />
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
            <DeskBadge desk={waiting.get(item.to)} />
          </NavLink>
        ))}

        <div className="mt-auto">
          <p className="px-3 text-[10.5px] leading-relaxed text-[#7590C6]">
            Every change here is recorded against your name. The rules that keep buyers and
            pharmacies safe are not configurable, and never will be.
          </p>

          {user ? (
            <div className="mt-4 border-t border-white/10 pt-3">
              <div className="flex items-center gap-2.5 px-3">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px] bg-white/[0.13] font-display text-[12px] font-bold text-white">
                  {initials(user.name)}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-[12px] font-bold text-white">{user.name}</div>
                  <div className="text-[10px] capitalize text-[#7590C6]">
                    {user.role.replace('_', ' ')}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={signOut}
                className="mt-1.5 flex min-h-11 w-full items-center gap-2.5 rounded-[9px] px-3 text-[12.5px] font-medium text-[#B9CBEC] hover:bg-white/[0.08] hover:text-white"
              >
                <Icon name="back" size={15} strokeWidth={2} />
                Sign out
              </button>
            </div>
          ) : null}
        </div>
      </nav>

      {/* Mobile: the rail becomes a top bar rather than disappearing. */}
      <div className="flex min-w-0 flex-1 flex-col">
        <nav
          aria-label="Admin"
          className="flex gap-1 overflow-x-auto bg-ink px-3 py-2.5 scrollbar-none md:hidden"
        >
          {visible.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                cn(
                  'flex shrink-0 items-center gap-1.5 rounded-[9px] px-3 py-2 text-xs font-medium no-underline',
                  isActive ? 'bg-white/[0.13] font-bold text-white' : 'text-[#B9CBEC]',
                )
              }
            >
              <Icon name={item.icon} size={15} strokeWidth={1.8} />
              {item.label}
              <DeskBadge desk={waiting.get(item.to)} />
            </NavLink>
          ))}

          {user ? (
            <button
              type="button"
              onClick={signOut}
              className="ml-auto flex shrink-0 items-center gap-1.5 rounded-[9px] px-3 py-2 text-xs font-medium text-[#B9CBEC]"
            >
              <Icon name="back" size={14} strokeWidth={2} />
              Sign out
            </button>
          ) : null}
        </nav>

        {/* ── ONE SOURCE FOR EVERYTHING WAITING ──────────────────────────
            This was the ordinary buyer's bell, on the reasoning that a
            pharmacy awaiting approval is somebody's job and would arrive as a
            notification like any other. It never did: nothing has ever written
            a staff notification row, so the bell was by construction always
            empty, and the queue counts stayed shut inside each desk's own
            screen.

            InboxBell reads what is actually waiting — derived from state, so
            it cannot be dismissed and cannot drift — and keeps the personal
            feed as a second section underneath, because an admin is also
            somebody with orders of their own. */}
        <div className="flex items-center justify-end border-b border-line bg-white px-3 py-1.5">
          <InboxBell />
        </div>

        <main className="min-w-0 flex-1">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

/**
 * The number beside a room's name.
 *
 * ── WHY IT IS A NUMBER AND NOT A DOT ───────────────────────────────────────
 *
 * A dot says "something". A number says whether to go now. "Support · 1" and
 * "Support · 23" are different working days and a dot renders them identically.
 *
 * ── AND WHY OVERDUE CHANGES THE COLOUR AND NOTHING ELSE ────────────────────
 *
 * Red here means "something in this room has waited longer than we promised" —
 * 48 hours on a reaction report, twenty minutes on a live chat. It is not a
 * second number, because two numbers on a menu item is a thing to decode
 * rather than read. The panel breaks it down for anybody who wants it.
 */
function DeskBadge({ desk }: { desk?: { count: number; overdue: number } }) {
  if (!desk || desk.count === 0) {
    return null;
  }

  return (
    <span
      data-testid="desk-badge"
      className={cn(
        'ml-auto shrink-0 rounded-full px-1.5 py-[1px] font-mono text-[10px] font-bold tabular-nums text-white',
        desk.overdue > 0 ? 'bg-danger' : 'bg-blue',
      )}
    >
      {desk.count > 99 ? '99+' : desk.count}
    </span>
  );
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);

  return ((parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')).toUpperCase() || 'CC';
}
